import { lazy, Suspense } from 'react';

import { ListSkeleton } from '@/shared/components/skeletons';
import { Skeleton } from '@/shared/ui/skeleton';

/* The heavy parts of the module (panels, the document tab, the settings tab) load on demand; these wrappers are what the registry renders. */

const TaskPanel = lazy(() => import('./components/TaskPanel'));
const InstancePanel = lazy(() => import('./components/InstancePanel'));
const DocumentProcessesTab = lazy(() =>
  import('./components/DocumentProcessesTab').then((m) => ({ default: m.DocumentProcessesTab })),
);
const ProcessesSettingsTab = lazy(() =>
  import('./components/ProcessesSettingsTab').then((m) => ({ default: m.ProcessesSettingsTab })),
);
const DocumentRobotsTab = lazy(() =>
  import('./components/DocumentRobotsTab').then((m) => ({ default: m.DocumentRobotsTab })),
);
const AutomationSection = lazy(() =>
  import('./components/automation/AutomationSection').then((m) => ({ default: m.AutomationSection })),
);
const BulkStartProcess = lazy(() =>
  import('./components/BulkStartProcess').then((m) => ({ default: m.BulkStartProcess })),
);

function PanelFallback() {
  return (
    <div className="space-y-4 p-6" aria-busy>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-40" />
      <ListSkeleton rows={4} avatar={false} />
    </div>
  );
}

export function LazyTaskPanel({ id }: { id: string }) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <TaskPanel key={id} id={id} />
    </Suspense>
  );
}

export function LazyInstancePanel({ id }: { id: string }) {
  return (
    <Suspense fallback={<PanelFallback />}>
      <InstancePanel key={id} id={id} />
    </Suspense>
  );
}

export function LazyDocumentProcessesTab({ type, id }: { type: string; id: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={2} avatar={false} />}>
      <DocumentProcessesTab key={`${type}:${id}`} type={type} id={id} />
    </Suspense>
  );
}

export function LazyProcessesSettingsTab({ type }: { type: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={3} />}>
      <ProcessesSettingsTab type={type} />
    </Suspense>
  );
}

export function LazyDocumentRobotsTab({ type, id }: { type: string; id: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={2} avatar={false} />}>
      <DocumentRobotsTab key={`${type}:${id}`} type={type} id={id} />
    </Suspense>
  );
}

export function LazyAutomationSection(props: { module?: string; categoriesSource?: string; documentType?: string }) {
  return (
    <Suspense fallback={<ListSkeleton rows={4} avatar={false} />}>
      <AutomationSection {...props} />
    </Suspense>
  );
}

export function LazyBulkStartProcess({
  type,
  ids,
  onDone,
}: {
  type: string;
  ids: readonly string[];
  onDone: () => void;
}) {
  return (
    <Suspense fallback={null}>
      <BulkStartProcess type={type} ids={ids} onDone={onDone} />
    </Suspense>
  );
}
